import { memo, useMemo } from 'react';

import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';

import { LeaderBoardIcon } from 'components';

import { useStyles } from './styles';

interface SimpleColumnChartProps {
  data?: number[];
  title: string;
  scale?: number;
  backgroundColor?: string;
  labels?: (string | number)[];
  levelBordered?: boolean;
}

const defaultBackground = '#D582F9';

function getLabelsFromScale(scale: number): string[] {
  return Array(scale)
    .fill(0)
    .map((_, index) => String(index + 1));
}

function SimpleColumnChartWithoutMemo({
  title,
  data,
  labels,
  levelBordered = false,
  scale = 5,
  backgroundColor = defaultBackground,
}: SimpleColumnChartProps): JSX.Element {
  const classes = useStyles();

  const memoLevelLabels = useMemo(() => {
    if (labels && labels.length === scale) {
      return labels;
    }

    return getLabelsFromScale(scale);
  }, [labels, scale]);

  const memoData = useMemo(() => {
    const internData = data ?? [];

    return Array(scale)
      .fill(0)
      .map((_, index) => internData[index] ?? 0);
  }, [scale, data]);

  return (
    <div
      className={clsx(classes.root, {
        [classes.noData]: !data,
      })}
    >
      {!!data && (
        <>
          <Typography className={classes.title}>{title}</Typography>
          <div
            className={classes.chartContainer}
            style={{ gridTemplateColumns: `repeat(${scale}, 1fr)` }}
          >
            <div className={classes.levelsContainer}>
              {getLabelsFromScale(5).map(level => (
                <div
                  key={level}
                  className={clsx(classes.level, {
                    bordered: levelBordered,
                  })}
                />
              ))}
            </div>

            {memoData.map(value => (
              <div key={uuid()} className={classes.barContainer}>
                <div
                  className={clsx(classes.bar, {
                    'no-radius': value < 4,
                  })}
                  style={{ height: `${value}%`, backgroundColor }}
                >
                  <Typography
                    className={classes.barLabel}
                    color="textSecondary"
                  >
                    {value}%
                  </Typography>
                </div>
              </div>
            ))}
          </div>
          <div
            className={classes.levelLabelsContainer}
            style={{
              gridTemplateColumns: `repeat(${memoLevelLabels.length}, 1fr)`,
            }}
          >
            {memoLevelLabels.map(label => (
              <div key={label} className={classes.levelLabelWrapper}>
                <Typography
                  className={classes.levelLabel}
                  color="textSecondary"
                >
                  {label}
                </Typography>
              </div>
            ))}
          </div>
        </>
      )}
      {!data && (
        <div className={classes.noDataIconWrapper}>
          <LeaderBoardIcon className={classes.noDataIcon} />
        </div>
      )}
    </div>
  );
}

export const SimpleColumnChart = memo(
  SimpleColumnChartWithoutMemo,
) as typeof SimpleColumnChartWithoutMemo;
